import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';

function MyApplications() {
  const [applications, setApplications] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Fetch user applications on component mount
  useEffect(() => {
    fetchMyApplications();
  }, []);

  const fetchMyApplications = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.get(
        'https://healthcare.visionkraftconsulting.com/api/applicants/my-applications',
        { withCredentials: true }
      );

      if (response.data && response.data.success) {
        setApplications(response.data.applications || []);
        setUser(response.data.user);
      } else {
        setApplications([]);
      }
    } catch (err) {
      console.error('Error fetching my applications:', err);
      if (err.response?.status === 401) {
        setError('Please log in to view your applications.');
      } else {
        setError('Failed to load your applications. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Get application status counts
  const getStatusCounts = () => {
    return applications.reduce((counts, app) => {
      const status = app.status || 'submitted';
      counts[status] = (counts[status] || 0) + 1;
      counts.total = (counts.total || 0) + 1;
      return counts;
    }, {});
  };

  // Get latest application
  const getLatestApplication = () => {
    if (applications.length === 0) return null;
    return applications.sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt))[0];
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status badge class
  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved': return 'bg-success';
      case 'rejected': return 'bg-danger';
      case 'under review': return 'bg-warning';
      case 'interview scheduled': return 'bg-info';
      default: return 'bg-secondary';
    }
  };

  const statusCounts = getStatusCounts();
  const latestApplication = getLatestApplication();

  if (loading) {
    return (
      <div className="text-center p-4">
        <Spinner animation="border" variant="primary" size="sm" />
        <span className="ms-2">Loading your applications...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-warning">
        <i className="bi bi-exclamation-triangle me-2"></i>
        {error}
      </div>
    );
  }

  return (
    <div className="my-applications-widget">
      {/* Quick Stats */}
      <div className="row mb-4">
        <div className="col-md-4 mb-3">
          <div className="card border-primary">
            <div className="card-body text-center">
              <i className="bi bi-file-earmark-text text-primary fs-1"></i>
              <h5 className="card-title mt-2">Total Applications</h5>
              <p className="card-text fs-3 fw-bold text-primary">{statusCounts.total || 0}</p>
            </div>
          </div>
        </div>
        <div className="col-md-4 mb-3">
          <div className="card border-warning">
            <div className="card-body text-center">
              <i className="bi bi-hourglass-split text-warning fs-1"></i>
              <h5 className="card-title mt-2">Under Review</h5>
              <p className="card-text fs-3 fw-bold text-warning">
                {statusCounts['under review'] || statusCounts.submitted || 0}
              </p>
            </div>
          </div>
        </div>
        <div className="col-md-4 mb-3">
          <div className="card border-success">
            <div className="card-body text-center">
              <i className="bi bi-check-circle text-success fs-1"></i>
              <h5 className="card-title mt-2">Approved</h5>
              <p className="card-text fs-3 fw-bold text-success">{statusCounts.approved || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Latest Application Status */}
      {latestApplication && (
        <div className="card mb-4">
          <div className="card-header bg-light">
            <h5 className="mb-0">
              <i className="bi bi-clock-history me-2"></i>
              Latest Application Status
            </h5>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-6">
                <p><strong>Application ID:</strong> #{latestApplication.id}</p>
                <p><strong>Position:</strong> {latestApplication.position}</p>
                <p><strong>Submitted:</strong> {formatDate(latestApplication.submittedAt)}</p>
              </div>
              <div className="col-md-6">
                <p><strong>Status:</strong> 
                  <span className={`badge ms-2 ${getStatusBadgeClass(latestApplication.status)}`}>
                    {latestApplication.status || 'Submitted'}
                  </span>
                </p>
                <p><strong>Last Updated:</strong> {formatDate(latestApplication.updatedAt)}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Applications List */}
      {applications.length > 0 ? (
        <div className="card">
          <div className="card-header bg-light d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <i className="bi bi-list-ul me-2"></i>
              All Applications
            </h5>
            <button className="btn btn-outline-primary btn-sm" onClick={fetchMyApplications}>
              <i className="bi bi-arrow-clockwise me-1"></i>
              Refresh
            </button>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>ID</th>
                    <th>Position</th>
                    <th>Status</th>
                    <th>Submitted</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {applications.map((app) => (
                    <tr key={app.id}>
                      <td>#{app.id}</td>
                      <td>{app.position}</td>
                      <td>
                        <span className={`badge ${getStatusBadgeClass(app.status)}`}>
                          {app.status || 'Submitted'}
                        </span>
                      </td>
                      <td>{formatDate(app.submittedAt)}</td>
                      <td>
                        <Link 
                          to="/applicant-dashboard" 
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => {
                            // Store application info for detailed view
                            localStorage.setItem('applicantInfo', JSON.stringify({
                              name: user?.name || app.name,
                              email: user?.email || app.email,
                              submittedAt: app.submittedAt
                            }));
                          }}
                        >
                          <i className="bi bi-eye me-1"></i>
                          View Details
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-body text-center py-5">
            <i className="bi bi-file-earmark-plus text-muted" style={{ fontSize: '4rem' }}></i>
            <h4 className="mt-3 text-muted">No Applications Found</h4>
            <p className="text-muted">You haven't submitted any job applications yet.</p>
            <Link to="/job-application" className="btn btn-primary">
              <i className="bi bi-plus-circle me-2"></i>
              Submit Your First Application
            </Link>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="row mt-4">
        <div className="col-md-6">
          <Link to="/job-application" className="btn btn-success w-100">
            <i className="bi bi-plus-circle me-2"></i>
            Submit New Application
          </Link>
        </div>
        <div className="col-md-6">
          <Link to="/applicant-dashboard" className="btn btn-outline-primary w-100">
            <i className="bi bi-search me-2"></i>
            Detailed Tracking
          </Link>
        </div>
      </div>
    </div>
  );
}

export default MyApplications;